/* Brands Only — predictive search. Fetches the bo-predictive section via the
   Section Rendering API and shows a live dropdown. Scope/depth come from data-attrs
   (set from theme settings). Progressive enhancement: the form still works without JS. */
(function () {
  if (customElements.get('bo-search')) return;

  customElements.define('bo-search', class BoSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.bo-suggest');
      if (!this.form || !this.input || !this.panel) return;

      this.predictive = this.dataset.predictive !== 'false';
      this.types = this.dataset.types || 'product';
      this.fields = this.dataset.fields || 'title';
      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.minChars = 2;
      this.controller = null;
      this.debounceId = null;
      this.activeIndex = -1;

      if (!this.predictive) return;

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length >= this.minChars) this.search(); });
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    onInput() {
      const q = this.input.value.trim();
      clearTimeout(this.debounceId);
      if (q.length < this.minChars) { this.close(); return; }
      this.debounceId = setTimeout(() => this.search(), 220);
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < this.minChars) { this.close(); return; }

      const params = new URLSearchParams();
      params.set('q', q);
      params.set('section_id', 'bo-predictive');
      params.set('resources[type]', this.types);
      params.set('resources[limit]', this.limit);
      params.set('resources[options][fields]', this.fields);
      params.set('resources[options][unavailable_products]', 'last');

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      try {
        const res = await fetch('/search/suggest?' + params.toString(), { signal: this.controller.signal });
        if (!res.ok) { this.close(); return; }
        const text = await res.text();
        // Section Rendering API returns the section wrapped in a div; extract its inner content.
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const rendered = doc.querySelector('.bo-suggest__inner, .bo-suggest__empty');
        this.panel.innerHTML = rendered ? doc.body.innerHTML : text;
        this.open();
      } catch (e) {
        if (e.name !== 'AbortError') this.close();
      }
    }

    open() {
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.activeIndex = -1;
      this.options = Array.prototype.slice.call(
        this.panel.querySelectorAll('.bo-suggest__product, .bo-suggest__link, .bo-suggest__all')
      );
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
      this.options = [];
    }

    setActive(i) {
      if (!this.options || !this.options.length) return;
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].removeAttribute('aria-selected');
        this.options[this.activeIndex].classList.remove('is-active');
      }
      this.activeIndex = (i + this.options.length) % this.options.length;
      const el = this.options[this.activeIndex];
      el.setAttribute('aria-selected', 'true');
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(e) {
      if (this.panel.hidden) return;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.setActive(this.activeIndex + 1); break;
        case 'ArrowUp': e.preventDefault(); this.setActive(this.activeIndex - 1); break;
        case 'Enter':
          if (this.activeIndex > -1 && this.options[this.activeIndex]) {
            e.preventDefault();
            window.location.href = this.options[this.activeIndex].getAttribute('href');
          }
          break;
        case 'Escape': this.close(); this.input.blur(); break;
      }
    }

    onDocClick(e) {
      if (!this.contains(e.target)) this.close();
    }
  });
})();
